import type { ApolloClient } from "@apollo/client";

import {
  type GetIssueQuery,
  Participant,
} from "@medbillai/generated-graphql-types";

import { errorHandling } from "../../../lib/apollo/utils";
import { getLoadingAiResponseEventId } from "./loadingEvents";
import {
  getIssueEventByIdQuery,
  getIssueEventByIdQueryVariables,
  getIssueQuery,
  getIssueQueryVariables,
  getIssueTurnTailQuery,
  getIssueTurnTailQueryVariables,
} from "./queries";
import { isStaleRead, markReadApplied, startRead } from "./readOrder";
import type { TimelineFetch } from "./rowFetch";
import { orderEventsBySentAt } from "./streamingChatUtils";

type GetIssueIssue = NonNullable<GetIssueQuery["issue__Issue"]>[number];
type GetIssueEvent = NonNullable<GetIssueIssue["userVisibleEvents"]>[number];

/**
 * An issue read by one of the narrow queries. Its events must assign to
 * GetIssue's own event type, which turns a selection that drifted from
 * GetIssue's into a compile error where the read is merged.
 */
export type NarrowIssue = Partial<Omit<GetIssueIssue, "userVisibleEvents">> & {
  readonly userVisibleEvents?: readonly GetIssueEvent[] | null;
};

export type MergeResult =
  | { readonly complete: true; readonly data: GetIssueQuery }
  | { readonly complete: false };

const INCOMPLETE: MergeResult = { complete: false };

/**
 * Newest server `sentAt` in the cached transcript, used as the tail's
 * lower bound. The streamed placeholder is stamped client-side, so
 * including it would skip every event the turn actually wrote. `excludeIds`
 * are rows read by id that may be newer than rows that went missing. The
 * member's rows are left out too: the client writes them itself, from the
 * send and the upload, so they say nothing about the rows before them.
 */
export const newestCachedSentAt = (
  cached: GetIssueQuery | null | undefined,
  loadingEventId: string,
  excludeIds: ReadonlySet<string> = new Set(),
): string | null => {
  const events = cached?.issue__Issue?.[0]?.userVisibleEvents ?? [];
  let newest: string | null = null;
  let newestMs = Number.NEGATIVE_INFINITY;
  for (const event of events) {
    if (
      event.id === loadingEventId ||
      excludeIds.has(event.id) ||
      event.participant === Participant.USER
    ) {
      continue;
    }
    const parsed = Date.parse(event.sentAt);
    if (Number.isNaN(parsed) || parsed < newestMs) {
      continue;
    }
    newest = event.sentAt;
    newestMs = parsed;
  }
  return newest;
};

/**
 * Fold a narrow read into the cached GetIssue data: add new ids, keep sentAt
 * order, refresh the issue fields, and drop the streamed placeholder once the
 * reply it stands in for has landed. A fresh read also replaces the rows it
 * names; a stale one only adds rows the cache lacks.
 */
export const mergeNarrowIssue = ({
  cached,
  narrow,
  loadingEventId,
  dropsLoadingEvent,
  refreshesKnownRows = false,
}: {
  cached: GetIssueQuery;
  narrow: NarrowIssue | undefined;
  loadingEventId: string;
  dropsLoadingEvent: boolean;
  refreshesKnownRows?: boolean;
}): MergeResult => {
  const cachedIssue = cached.issue__Issue?.[0];
  if (!cachedIssue || !narrow || narrow.id !== cachedIssue.id) {
    return INCOMPLETE;
  }
  const { userVisibleEvents, ...issueFields } = narrow;

  const readRows = new Map(
    (userVisibleEvents ?? []).map(event => [event.id, event]),
  );
  const kept = (cachedIssue.userVisibleEvents ?? [])
    .filter(event => !dropsLoadingEvent || event.id !== loadingEventId)
    .map(event =>
      refreshesKnownRows ? (readRows.get(event.id) ?? event) : event,
    );
  const knownIds = new Set(kept.map(event => event.id));
  const appended = (userVisibleEvents ?? []).filter(event => {
    if (knownIds.has(event.id)) {
      return false;
    }
    knownIds.add(event.id);
    return true;
  });

  const mergedIssue: GetIssueIssue = {
    ...cachedIssue,
    ...issueFields,
    userVisibleEvents: [...orderEventsBySentAt([...kept, ...appended])],
  };

  return {
    complete: true,
    data: {
      ...cached,
      issue__Issue: [mergedIssue, ...(cached.issue__Issue ?? []).slice(1)],
    },
  };
};

type CaseRead = {
  client: ApolloClient<unknown>;
  issueId: string;
  userId: string;
};

/**
 * Rows the by-id path merged since the last read that covered them: a tail
 * read, or a fresh full read. A row read by id says nothing about the rows
 * before it, so none of these may anchor the next tail, whichever plan asks
 * for it.
 */
const readByIdSinceCoveringRead = new Map<string, Set<string>>();

/** A fresh full read holds every row applied before it, so none can be skipped. */
export const forgetRowsReadById = (issueId: string): void => {
  readByIdSinceCoveringRead.delete(issueId);
};

/**
 * A narrow read that has not answered by then has failed, so the fallback
 * runs instead of waiting on a request that may never settle.
 */
export const NARROW_READ_DEADLINE_MS = 10_000;

export const withinReadDeadline = <T>(read: Promise<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("Transcript read passed its deadline")),
      NARROW_READ_DEADLINE_MS,
    );
    read.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

const readCachedIssue = ({ client, issueId, userId }: CaseRead) =>
  client.cache.readQuery({
    query: getIssueQuery,
    variables: getIssueQueryVariables(issueId, userId),
  });

/**
 * Fold into the GetIssue entry inside the update, since the cache can move
 * while a read is in flight. False when nothing is cached or the read does not
 * fit. A null `readNumber` is a cache-only write, which takes no place in the
 * read order.
 */
const writeIntoGetIssue = (
  { client, issueId, userId }: CaseRead,
  narrow: NarrowIssue | undefined,
  dropsLoadingEvent: boolean,
  readNumber: number | null,
): boolean => {
  const loadingEventId = getLoadingAiResponseEventId(issueId);
  // The reads bypass the cache, so only this ordered write can land them.
  const stale = readNumber !== null && isStaleRead(issueId, readNumber);
  const fitted =
    stale && narrow
      ? { id: narrow.id, userVisibleEvents: narrow.userVisibleEvents }
      : narrow;
  let merged = false;
  client.cache.updateQuery(
    {
      query: getIssueQuery,
      variables: getIssueQueryVariables(issueId, userId),
    },
    current => {
      if (!current) {
        return undefined;
      }
      const outcome = mergeNarrowIssue({
        cached: current,
        narrow: fitted,
        loadingEventId,
        dropsLoadingEvent,
        refreshesKnownRows: !stale,
      });
      if (!outcome.complete) {
        return undefined;
      }
      merged = true;
      return outcome.data;
    },
  );
  if (merged && !stale && readNumber !== null) {
    markReadApplied(issueId, readNumber);
  }
  return merged;
};

/**
 * Read the named rows, one query each, and fold them in with one cache update.
 * False means fall back: a failed read, or a named row that did not come back.
 */
export const applyEventsById = async (
  read: CaseRead & {
    eventIds: readonly string[];
    dropsLoadingEvent: boolean;
  },
): Promise<boolean> => {
  const { client, issueId, userId, eventIds, dropsLoadingEvent } = read;
  const readNumber = startRead(issueId);
  let issues: (NarrowIssue | undefined)[];
  try {
    issues = await withinReadDeadline(
      Promise.all(
        eventIds.map(async (eventId, index) => {
          const { data } = await client.query({
            query: getIssueEventByIdQuery,
            variables: getIssueEventByIdQueryVariables(
              issueId,
              userId,
              eventId,
              index === eventIds.length - 1,
            ),
            fetchPolicy: "no-cache",
            context: errorHandling("caller"),
          });
          return data.issue__Issue?.[0];
        }),
      ),
    );
  } catch {
    return false;
  }
  const rows = issues.flatMap(issue => issue?.userVisibleEvents ?? []);
  const returned = new Set(rows.map(row => row.id));
  const last = issues.at(-1);
  if (!last || eventIds.some(id => !returned.has(id))) {
    return false;
  }
  const merged = writeIntoGetIssue(
    read,
    { ...last, userVisibleEvents: rows },
    dropsLoadingEvent,
    readNumber,
  );
  if (merged) {
    const readIds = readByIdSinceCoveringRead.get(issueId) ?? new Set<string>();
    for (const id of eventIds) {
      readIds.add(id);
    }
    readByIdSinceCoveringRead.set(issueId, readIds);
  }
  return merged;
};

/** Read everything after the last contiguous row and fold it in. */
export const applyTurnTail = async (
  read: CaseRead & {
    excludeIds: readonly string[];
    dropsLoadingEvent: boolean;
  },
): Promise<boolean> => {
  const { client, issueId, userId, excludeIds, dropsLoadingEvent } = read;
  // Rows read by id while this tail is in flight may be newer than what it
  // returns, so only the ones it anchored below stop being excluded.
  const anchoredBelow = [...(readByIdSinceCoveringRead.get(issueId) ?? [])];
  const after = newestCachedSentAt(
    readCachedIssue(read),
    getLoadingAiResponseEventId(issueId),
    new Set([...excludeIds, ...anchoredBelow]),
  );
  if (!after) {
    return false;
  }
  const readNumber = startRead(issueId);
  try {
    const { data } = await withinReadDeadline(
      client.query({
        query: getIssueTurnTailQuery,
        variables: getIssueTurnTailQueryVariables(issueId, userId, after),
        fetchPolicy: "no-cache",
        context: errorHandling("caller"),
      }),
    );
    const merged = writeIntoGetIssue(
      read,
      data.issue__Issue?.[0],
      dropsLoadingEvent,
      readNumber,
    );
    if (merged) {
      const readIds = readByIdSinceCoveringRead.get(issueId);
      for (const id of anchoredBelow) {
        readIds?.delete(id);
      }
    }
    return merged;
  } catch {
    return false;
  }
};

/**
 * Run a planned batch against the cached transcript: the named rows by id,
 * then the since-last-seen read when the plan or a failed id read calls for
 * it. Resolves false when the caller should run the full GetIssue refetch.
 */
export const applyTimelineFetch = async ({
  fetch,
  ...read
}: CaseRead & { fetch: TimelineFetch }): Promise<boolean> => {
  const { dropsLoadingEvent } = fetch;
  let sinceLastSeen = fetch.sinceLastSeen;
  if (fetch.eventIds.length > 0) {
    const merged = await applyEventsById({
      ...read,
      eventIds: fetch.eventIds,
      dropsLoadingEvent,
    });
    if (!merged) {
      sinceLastSeen = sinceLastSeen ?? { excludeIds: [] };
    }
  }
  if (sinceLastSeen) {
    // The batch's own by-id rows run first and cannot anchor the tail.
    return applyTurnTail({
      ...read,
      excludeIds: [...sinceLastSeen.excludeIds, ...fetch.eventIds],
      dropsLoadingEvent,
    });
  }
  if (fetch.eventIds.length === 0 && dropsLoadingEvent) {
    // A turn that wrote nothing new still retires its placeholder, no read.
    const cachedIssueId = readCachedIssue(read)?.issue__Issue?.[0]?.id;
    if (cachedIssueId) {
      // Reads nothing, so an in-flight read must not count as stale behind it.
      writeIntoGetIssue(read, { id: cachedIssueId }, true, null);
    }
  }
  return true;
};
